import { type Routes } from '@angular/router';
import {
  HasWorkspaceGuard,
  IsAuthenticatedGuard,
} from '@principle-theorem/ng-auth';
import { FeatureEnabledGuard } from '@principle-theorem/ng-feature-flags';
import { SetupWizardGuard } from '@principle-theorem/ng-principle';
import {
  HasPermissionGuard,
  IsAuthorisedGuard,
  IsIpRestrictedGuard,
  IsManagementGuard,
  IsTimeRestrictedGuard,
  MultifactorEnforcementGuard,
} from '@principle-theorem/ng-principle-shared';
import {
  AI_RECEPTIONIST_FEATURE,
  APPOINTMENTS_FEATURE,
  AppointmentPermissions,
  BRANDS_FEATURE,
  BrandPermissions,
  CALENDAR_ROUTE_FEATURE,
  CHATS_FEATURE,
  CONTACTS_FEATURE,
  CONVERSATIONS_FEATURE,
  FOLLOW_UPS_FEATURE,
  FUNDING_REQUESTS_FEATURE,
  GAPS_FEATURE,
  LABS_FEATURE,
  LAB_JOBS_FEATURE,
  LEADS_FEATURE,
  MANAGEMENT_FEATURE,
  MARKETING_CAMPAIGNS_FEATURE,
  PATIENTS_FEATURE,
  PatientPermissions,
  REFERRALS_FEATURE,
  REPORTING_FEATURE,
  SCHEDULING_ALERTS_FEATURE,
  SEEDING_FEATURE,
  SETUP_WIZARD_FEATURE,
  STAFF_FEATURE,
  STERILISATION_FEATURE,
  TASKS_FEATURE,
} from '@principle-theorem/principle-core/features';
import { ExternalLinkRedirectComponent } from '../external-link/external-link-redirect/external-link-redirect.component';
import { BrandResolverService } from '../layout/brand-resolver.service';
import { MainComponent } from '../layout/main/main.component';
import { PortalPageComponent } from '../layout/portal-page/portal-page.component';
import { PracticeResolverService } from '../layout/practice-resolver.service';
import { PracticeSelectorComponent } from '../layout/practice-selector/practice-selector.component';
import { OrgIdGuard } from './org-id.guard';

const sharedAppGuards = [
  FeatureEnabledGuard,
  HasPermissionGuard,
  IsIpRestrictedGuard,
  IsTimeRestrictedGuard,
  MultifactorEnforcementGuard,
];

export const ORGANISATION_ROUTES: Routes = [
  {
    path: 'reporting',
    canActivate: [IsAuthenticatedGuard, HasWorkspaceGuard, ...sharedAppGuards],
    data: {
      title: 'Reporting',
      feature: REPORTING_FEATURE,
    },
    loadChildren: () =>
      import('@principle-theorem/ng-reporting/pages').then(
        (mod) => mod.ReportingRoutingModule
      ),
  },
];

export const PRACTICE_SCOPED_ROUTES: Routes = [
  {
    path: '',
    redirectTo: 'schedule/timeline',
    pathMatch: 'full',
  },
  {
    path: 'dashboard',
    canActivate: sharedAppGuards,
    loadChildren: () =>
      import('../dashboard/dashboard-routing.module').then(
        (mod) => mod.DashboardRoutingModule
      ),
    data: {
      feature: APPOINTMENTS_FEATURE,
      permissions: [AppointmentPermissions.AppointmentManage],
    },
  },
  {
    path: 'setup',
    canActivate: [...sharedAppGuards, SetupWizardGuard],
    loadChildren: () =>
      import('@principle-theorem/ng-principle/pages').then(
        (mod) => mod.SetupWizardRoutingModule
      ),
    data: {
      feature: SETUP_WIZARD_FEATURE,
    },
  },
  {
    path: 'gaps',
    canActivate: sharedAppGuards,
    loadChildren: () =>
      import('@principle-theorem/ng-gaps').then(
        (mod) => mod.GapPagesRoutingModule
      ),
    data: {
      title: 'Appointment Gaps',
      feature: GAPS_FEATURE,
    },
  },
  {
    path: 'appointment-requests',
    loadComponent: () =>
      import('@principle-theorem/ng-appointment/pages').then(
        (mod) => mod.AppointmentRequestsComponent
      ),
    canActivate: sharedAppGuards,
    data: {
      title: 'Appointment Requests',
      feature: APPOINTMENTS_FEATURE,
      permissions: [AppointmentPermissions.AppointmentManage],
    },
  },
  {
    path: 'follow-ups',
    canActivate: sharedAppGuards,
    data: {
      title: 'Appointment Follow Ups',
      feature: FOLLOW_UPS_FEATURE,
    },
    loadChildren: () =>
      import('@principle-theorem/ng-follow-ups/pages').then(
        (mod) => mod.FollowUpPagesRoutingModule
      ),
  },
  {
    path: 'scheduling-alerts',
    loadComponent: () =>
      import('../scheduling-alerts/scheduling-alerts.component').then(
        (mod) => mod.SchedulingAlertsComponent
      ),
    canActivate: sharedAppGuards,
    data: {
      title: 'Scheduling Alerts',
      feature: SCHEDULING_ALERTS_FEATURE,
    },
  },
  {
    path: 'lab-jobs',
    loadChildren: () =>
      import('@principle-theorem/ng-labs/pages').then(
        (mod) => mod.LabJobPagesRoutingModule
      ),
    canActivate: sharedAppGuards,
    data: {
      title: 'Lab Jobs',
      feature: LAB_JOBS_FEATURE,
    },
  },
  {
    path: 'contacts',
    canActivate: sharedAppGuards,
    data: {
      title: 'Contacts',
      feature: CONTACTS_FEATURE,
    },
    loadChildren: () =>
      import('@principle-theorem/ng-contacts').then(
        (mod) => mod.ContactPagesRoutingModule
      ),
  },
  {
    path: 'tasks',
    canActivate: sharedAppGuards,
    data: {
      feature: TASKS_FEATURE,
    },
    loadChildren: () =>
      import('@principle-theorem/ng-tasks/pages').then(
        (mod) => mod.TaskPagesRoutingModule
      ),
  },
  {
    path: 'labs',
    canActivate: sharedAppGuards,
    data: {
      feature: LABS_FEATURE,
    },
    loadChildren: () =>
      import('@principle-theorem/ng-labs/pages').then(
        (mod) => mod.LabPagesRoutingModule
      ),
  },
  {
    path: 'schedule',
    canActivate: sharedAppGuards,
    data: { feature: CALENDAR_ROUTE_FEATURE },
    loadChildren: () =>
      import('@principle-theorem/ng-calendar/pages').then(
        (mod) => mod.NgCalendarRoutingModule
      ),
  },
  {
    path: 'sterilisation',
    canActivate: sharedAppGuards,
    data: { feature: STERILISATION_FEATURE },
    loadChildren: () =>
      import('@principle-theorem/ng-sterilisation').then(
        (mod) => mod.NgSterilisationRoutingModule
      ),
  },
  {
    path: 'conversations',
    canActivate: sharedAppGuards,
    data: { feature: CONVERSATIONS_FEATURE },
    loadChildren: () =>
      import('@principle-theorem/ng-conversations/pages').then(
        (mod) => mod.NgConversationsRoutingModule
      ),
  },
  {
    path: 'chats',
    canActivate: sharedAppGuards,
    data: { feature: CHATS_FEATURE },
    loadChildren: () =>
      import('@principle-theorem/ng-chats/pages').then(
        (mod) => mod.NgChatsRoutingModule
      ),
  },
  {
    path: 'funding-requests',
    canActivate: sharedAppGuards,
    data: { feature: FUNDING_REQUESTS_FEATURE },
    loadChildren: () =>
      import('@principle-theorem/ng-funding-requests/pages').then(
        (mod) => mod.NgFundingRequestsRoutingModule
      ),
  },
];

export const APP_ROUTES: Routes = [
  {
    path: '',
    canActivate: [
      IsAuthenticatedGuard,
      IsAuthorisedGuard,
      HasWorkspaceGuard,
      MultifactorEnforcementGuard,
    ],
    component: PracticeSelectorComponent,
  },
  {
    path: '',
    loadChildren: () =>
      import('@principle-theorem/ng-principle/pages').then(
        (mod) => mod.AuthPagesRoutingModule
      ),
  },
  {
    path: 'redirect',
    canActivate: [
      IsAuthenticatedGuard,
      IsAuthorisedGuard,
      HasWorkspaceGuard,
      MultifactorEnforcementGuard,
    ],
    component: ExternalLinkRedirectComponent,
  },
  {
    path: 'portal',
    component: PortalPageComponent,
    children: [
      {
        path: '',
        loadChildren: () =>
          import('@principle-theorem/ng-patient/pages').then(
            (mod) => mod.NgPatientActionsRoutingModule
          ),
      },
      {
        path: 'booking',
        loadChildren: () =>
          import('@principle-theorem/ng-patient-scheduling-portal').then(
            (mod) => mod.NgPatientSchedulingPortalRoutingModule
          ),
      },
    ],
  },
  {
    path: 'link/:token',
    component: ExternalLinkRedirectComponent,
  },
  {
    path: 'unsubscribe/:token',
    redirectTo: '/portal/unsubscribe/:token',
  },
  {
    path: 'confirm/:token',
    redirectTo: '/portal/confirm-appointment/:token',
  },
  {
    path: 'history/:token',
    redirectTo: '/portal/medical-history/:token',
  },
  {
    path: 'invoice/:token',
    redirectTo: '/portal/view-invoice/:token',
  },
  {
    path: 'reschedule/:token',
    redirectTo: '/portal/appointment-reschedule/:token',
  },
  {
    path: 'forms/:token',
    redirectTo: '/portal/forms/:token',
  },
  {
    path: 'settings',
    canActivate: [IsAuthenticatedGuard, IsAuthorisedGuard, ...sharedAppGuards],
    loadChildren: () =>
      import('@principle-theorem/ng-principle/pages').then(
        (mod) => mod.SettingsPagesRoutingModule
      ),
  },
  {
    path: '',
    canActivate: [IsAuthenticatedGuard, IsAuthorisedGuard, OrgIdGuard],
    children: [
      {
        path: '',
        component: MainComponent,
        canActivate: [
          HasWorkspaceGuard,
          IsIpRestrictedGuard,
          IsTimeRestrictedGuard,
          MultifactorEnforcementGuard,
        ],
        children: [
          ...ORGANISATION_ROUTES,
          {
            path: 'mock-seeder',
            canActivate: [FeatureEnabledGuard],
            data: {
              title: 'Mock Seeder',
              feature: SEEDING_FEATURE,
            },
            loadChildren: () =>
              import('../mock-seeder/mock-seeder-routing.module').then(
                (mod) => mod.MockSeederRoutingModule
              ),
          },
          {
            path: 'management',
            canActivate: [FeatureEnabledGuard, IsManagementGuard],
            data: {
              feature: MANAGEMENT_FEATURE,
            },
            loadChildren: () =>
              import('@principle-theorem/ng-principle/pages').then(
                (mod) => mod.ManagementPagesRoutingModule
              ),
          },
          {
            path: 'tests',
            canActivate: [IsAuthenticatedGuard, ...sharedAppGuards],
            loadChildren: () =>
              import('@principle-theorem/ng-principle/pages').then(
                (mod) => mod.TestPagesRoutingModule
              ),
          },
          {
            path: 'account',
            loadChildren: () =>
              import('@principle-theorem/ng-principle/pages').then(
                (mod) => mod.AccountRoutingModule
              ),
          },
          {
            path: ':brand-slug',
            resolve: {
              brand: BrandResolverService,
            },
            children: [
              {
                path: '',
                redirectTo: 'patients',
                pathMatch: 'full',
              },
              {
                path: 'patients',
                canActivate: sharedAppGuards,
                data: {
                  title: 'Patients',
                  feature: PATIENTS_FEATURE,
                },
                loadChildren: () =>
                  import('../patients/patients-routing.module').then(
                    (mod) => mod.PatientsRoutingModule
                  ),
              },
              {
                path: 'ai-suite',
                canActivate: sharedAppGuards,
                data: {
                  title: 'AI Suite',
                  feature: AI_RECEPTIONIST_FEATURE,
                },
                loadChildren: () =>
                  import('@principle-theorem/ng-ai-suite').then(
                    (mod) => mod.AiSuiteRoutingModule
                  ),
              },
              {
                path: 'leads',
                canActivate: sharedAppGuards,
                data: {
                  title: 'Leads',
                  feature: LEADS_FEATURE,
                  permissions: [BrandPermissions.LeadsManage],
                },
                loadComponent: () =>
                  import('@principle-theorem/ng-principle/pages').then(
                    (mod) => mod.LeadsListComponent
                  ),
              },
              {
                path: 'staff',
                canActivate: sharedAppGuards,
                data: {
                  title: 'Staff',
                  feature: STAFF_FEATURE,
                },
                loadChildren: () =>
                  import('../staff/staff-routing.module').then(
                    (mod) => mod.StaffRoutingModule
                  ),
              },
              {
                path: 'automations',
                canActivate: sharedAppGuards,
                data: {
                  title: 'Automations',
                  feature: BRANDS_FEATURE,
                  permissions: [BrandPermissions.AutomationManage],
                },
                loadChildren: () =>
                  import('@principle-theorem/ng-automations/pages').then(
                    (mod) => mod.PagesRoutingModule
                  ),
              },
              {
                path: 'referrals',
                canActivate: sharedAppGuards,
                data: {
                  title: 'Referrals',
                  feature: REFERRALS_FEATURE,
                  permissions: [PatientPermissions.ReferralManage],
                },
                loadChildren: () =>
                  import('@principle-theorem/ng-referral').then(
                    (mod) => mod.referralRoutes
                  ),
              },
              {
                path: 'migration',
                loadChildren: () =>
                  import('@principle-theorem/ng-practice-migrations').then(
                    (mod) => mod.PracticeMigrationsPortalPagesRoutingModule
                  ),
              },
              {
                path: 'campaigns',
                canActivate: [FeatureEnabledGuard, HasPermissionGuard],
                data: {
                  title: 'Marketing Campaigns',
                  feature: MARKETING_CAMPAIGNS_FEATURE,
                  permissions: [BrandPermissions.MarketingCampaignsManage],
                },
                loadChildren: () =>
                  import('@principle-theorem/ng-marketing').then(
                    (mod) => mod.NgMarketingRoutingModule
                  ),
              },
              {
                path: ':practice-slug',
                children: [...PRACTICE_SCOPED_ROUTES],
                resolve: { practice: PracticeResolverService },
              },
              {
                path: 'appointments',
                loadChildren: () =>
                  import('@principle-theorem/ng-appointment/pages').then(
                    (mod) => mod.NgAppointmentSchedulingRoutingModule
                  ),
              },
            ],
          },
        ],
      },
    ],
  },
];
